import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { apiFetch } from '../../services/api';
import Logo from '../../assets/images/Logo.svg';
import toast from 'react-hot-toast';

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: email, 2: OTP, 3: new password
  const [formData, setFormData] = useState({
    email: '',
    otp: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const handleRequestOTP = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await apiFetch('/api/auth/forgot-password', {
        method: 'POST',
        auth: false,
        body: { email: formData.email }
      });

      if (response.success !== false) {
        toast.success('OTP sent to your email');
        setStep(2);
      } else {
        setError(response.error || 'Failed to send OTP');
      }
    } catch (err) {
      setError(err?.data?.error || err.message || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await apiFetch('/api/auth/verify-reset-otp', {
        method: 'POST',
        auth: false,
        body: { email: formData.email, otp: formData.otp }
      });

      if (response.success !== false) {
        toast.success('OTP verified successfully');
        setStep(3);
      } else {
        setError(response.error || 'Invalid OTP');
      }
    } catch (err) {
      setError(err?.data?.error || err.message || 'Invalid OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.newPassword !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      const response = await apiFetch('/api/auth/reset-password', {
        method: 'POST',
        auth: false,
        body: {
          email: formData.email,
          otp: formData.otp,
          newPassword: formData.newPassword
        }
      });

      if (response.success !== false) {
        toast.success('Password reset successfully');
        navigate('/login');
      } else {
        setError(response.error || 'Failed to reset password');
      }
    } catch (err) {
      setError(err?.data?.error || err.message || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white p-4 flex items-center justify-center overflow-y-auto">
      <div className="w-full max-w-[1400px] flex flex-col lg:flex-row gap-6 min-h-full py-4">
        {/* Left Side - Form */}
        <div className="w-full lg:w-[606px] bg-white backdrop-blur-[8.65px] rounded-xl p-4 sm:p-6 flex flex-col lg:min-h-[600px] lg:max-h-[881px] mx-auto lg:mx-0">
          {/* Logo */}
          <div className="flex justify-center mb-4 lg:mb-0 flex-shrink-0">
            <img src={Logo} alt="ProFound" className="h-8 sm:h-10 w-auto max-w-[157px]" />
          </div>

          {/* Form Content */}
          <div className="flex-1 flex flex-col min-h-0 px-4 sm:px-9 py-4 lg:py-0">
            <div className="flex flex-col gap-4 sm:gap-6 items-center flex-1 min-h-0">
              {/* Title */}
              <div className="text-center space-y-2 w-full flex-shrink-0">
                <h1 className="text-2xl sm:text-[32px] font-medium text-[#212529] tracking-[-0.64px] leading-[1.4]">
                  {step === 1 && 'Forgot Password?'}
                  {step === 2 && 'Enter OTP'}
                  {step === 3 && 'Reset Password'}
                </h1>
                <p className="text-xs sm:text-sm text-[#73827c] leading-[1.7]">
                  {step === 1 && 'Enter your email address and we\'ll send you an OTP to reset your password.'}
                  {step === 2 && 'Enter the 6-digit OTP sent to your email address.'}
                  {step === 3 && 'Enter your new password below.'}
                </p>
              </div>

              {/* Form */}
              <form 
                onSubmit={
                  step === 1 ? handleRequestOTP :
                  step === 2 ? handleVerifyOTP :
                  handleResetPassword
                } 
                className="w-full space-y-4 sm:space-y-5 flex-1 flex flex-col min-h-0"
              >
                <div className="flex-1 min-h-0 overflow-y-auto space-y-4 sm:space-y-5 pr-1">
                  {error && (
                    <div className="w-full px-3 py-2 rounded bg-red-50 text-red-700 text-sm border border-red-200 flex-shrink-0">
                      {error}
                    </div>
                  )}

                  {/* Step 1: Email */}
                  {step === 1 && (
                    <div className="space-y-1 flex-shrink-0">
                      <label className="text-sm text-[#485b54] leading-[1.7]">
                        Email*
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="eg. example123@email.com"
                        className="w-full px-3 py-2 border border-[#ebebeb] rounded-lg text-sm text-[#73827c] placeholder:text-[#73827c] focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent"
                        required
                      />
                    </div>
                  )}

                  {/* Step 2: OTP */}
                  {step === 2 && (
                    <div className="space-y-1 flex-shrink-0">
                      <label className="text-sm text-[#485b54] leading-[1.7]">
                        OTP Code*
                      </label>
                      <input
                        type="text"
                        name="otp"
                        value={formData.otp}
                        onChange={handleChange}
                        placeholder="Enter 6-digit OTP"
                        maxLength="6"
                        className="w-full px-3 py-2 border border-[#ebebeb] rounded-lg text-[#73827c] placeholder:text-[#73827c] focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent text-center text-2xl tracking-widest"
                        required
                      />
                      <p className="text-xs text-[#73827c] text-center mt-2">
                        Didn't receive the code?{' '}
                        <button
                          type="button"
                          onClick={handleRequestOTP}
                          className="text-[#40c191] hover:underline"
                          disabled={loading}
                        >
                          Resend OTP
                        </button>
                      </p>
                    </div>
                  )}

                  {/* Step 3: New Password */}
                  {step === 3 && (
                    <>
                      <div className="space-y-1 flex-shrink-0">
                        <label className="text-sm text-[#485b54] leading-[1.7]">
                          New Password*
                        </label>
                        <input
                          type="password"
                          name="newPassword"
                          value={formData.newPassword}
                          onChange={handleChange}
                          placeholder="Enter new password"
                          className="w-full px-3 py-2 border border-[#ebebeb] rounded-lg text-sm text-[#73827c] placeholder:text-[#73827c] focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent"
                          required
                          minLength={6}
                        />
                      </div>
                      <div className="space-y-1 flex-shrink-0">
                        <label className="text-sm text-[#485b54] leading-[1.7]">
                          Confirm Password*
                        </label>
                        <input
                          type="password"
                          name="confirmPassword"
                          value={formData.confirmPassword}
                          onChange={handleChange}
                          placeholder="Confirm new password"
                          className="w-full px-3 py-2 border border-[#ebebeb] rounded-lg text-sm text-[#73827c] placeholder:text-[#73827c] focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent"
                          required
                          minLength={6}
                        />
                      </div>
                    </>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex-shrink-0 space-y-3 sm:space-y-4 pt-2">
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full h-10 sm:h-11 bg-gradient-to-r from-[#40c191] to-[#5ed58d] text-white text-sm font-medium rounded-lg hover:opacity-90 transition-opacity shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? 'Processing...' : 
                     step === 1 ? 'Send OTP' :
                     step === 2 ? 'Verify OTP' :
                     'Reset Password'}
                  </button>

                  {step > 1 && (
                    <button
                      type="button"
                      onClick={() => setStep(step - 1)}
                      className="w-full h-10 sm:h-11 border border-[#ebebeb] text-[#485b54] text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors"
                      disabled={loading}
                    >
                      Back
                    </button>
                  )}

                  <div className="flex items-center justify-center">
                    <Link to="/login" className="text-xs sm:text-sm text-[#485b54] underline hover:text-emerald-400">
                      Back to Login
                    </Link>
                  </div>
                </div>
              </form>
            </div>
          </div>

          {/* Footer */}
          <div className="text-center mt-4 lg:mt-0 flex-shrink-0 hidden sm:block">
            <p className="text-xs sm:text-sm text-[#485b54] leading-[1.7]">All Rights Reserved © 2025</p>
          </div>
        </div>

        {/* Right Side - Gradient Background */}
        <div className="hidden lg:flex flex-1 rounded-xl relative overflow-hidden bg-gradient-to-br from-cyan-300 via-emerald-200 to-emerald-100">
          <div className="absolute inset-0">
            <div className="absolute inset-0 bg-gradient-to-br from-[#7DD3DD] via-[#A8E6CF] to-[#C8F7DC]"></div>
            <div className="absolute inset-0 opacity-40 bg-[radial-gradient(circle_at_30%_50%,rgba(125,211,221,0.4)_0%,transparent_50%)]"></div>
            <div className="absolute inset-0 opacity-40 bg-[radial-gradient(circle_at_70%_50%,rgba(168,230,207,0.4)_0%,transparent_50%)]"></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;

